import React, { useEffect } from 'react';
import './styles/app.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { IframeAuthProvider, useIframeAuth } from './contexts/IframeAuthContext';
import { IframeLoading } from './components/IframeLoading';
import { Navigation } from './components/Navigation';
import { ViewerView } from './views/ViewerView';
import { ResourceView } from './views/ResourceView';
import { UpdatesView } from './views/UpdatesView';
import { RadarView } from './views/RadarView';
import { NotFoundView } from './views/NotFoundView';
import { LoginView } from './views/LoginView';
import { RegisterView } from './views/RegisterView';
import { AddTenantView } from './views/AddTenantView';
import { InboxView as SparkInboxView } from './features/spark/views/InboxView';
import { CreativeSearchView } from './features/spark/views/CreativeSearchView';
import { ProjectView as SparkProjectView } from './features/spark/views/ProjectView';
import { CreativeEditor } from './features/spark/components/modals/CreativeEditor';

// Import react-toastify
import { ToastContainer } from 'react-toastify';
import SparkPollingProvider from './features/spark/providers/SparkPollingProvider';
import 'react-toastify/dist/ReactToastify.css';

// Redirect home to Radar view
const HomeRedirect: React.FC = () => {
  return <Navigate to="/radar" replace />;
};

// Wrap the app content that requires authentication
const AuthenticatedApp: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { isIframeMode, iframeAuthStatus, initializeIframeAuth, isReauthenticating } = useIframeAuth();
  const location = useLocation();

  const isIframeOnlyMode = process.env.REACT_APP_IFRAME_ONLY_MODE === 'true';

  // Check URL params for iframe customization
  const urlParams = new URLSearchParams(window.location.search);
  const hideNav = urlParams.get('hideNav') === 'true' || urlParams.get('hidenav') === 'true';
  const embedPath = urlParams.get('embedPath') || urlParams.get('embed');

  useEffect(() => {
    if (isIframeMode && !isAuthenticated && iframeAuthStatus === 'idle') {
      initializeIframeAuth();
    }
  }, [iframeAuthStatus, initializeIframeAuth, isAuthenticated, isIframeMode]);

  // Check if we should navigate to embedded path
  const shouldNavigateToEmbed = isAuthenticated && embedPath && location.pathname === '/';

  useEffect(() => {
    const className = 'iframe-no-background';
    const { body } = document;

    if (hideNav) {
      body.classList.add(className);
    } else {
      body.classList.remove(className);
    }

    return () => {
      body.classList.remove(className);
    };
  }, [hideNav]);

  if (!isAuthenticated) {
    console.log('[App] Not authenticated, checking iframe mode:', {
      isIframeMode,
      isReauthenticating,
      iframeAuthStatus,
      location: window.location.href
    });

    if (isIframeMode || isReauthenticating) {
      // Show loading screen during iframe auth or re-authentication
      if (iframeAuthStatus === 'requesting' || iframeAuthStatus === 'authenticating' || iframeAuthStatus === 'idle' || isReauthenticating) {
        console.log('[App] Showing IframeLoading');
        return <IframeLoading showFallback={!isIframeOnlyMode} />;
      }

      if (iframeAuthStatus === 'error') {
        return <IframeLoading showFallback={!isIframeOnlyMode} />;
      }

      if (isIframeOnlyMode) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              textAlign: 'center',
              padding: '20px',
            }}
          >
            <h2>Access Restricted</h2>
            <p>This application can only be accessed through the authorized business application.</p>
            <p>Please contact your administrator for access.</p>
          </div>
        );
      }
    } else if (isIframeOnlyMode) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            textAlign: 'center',
            padding: '20px',
          }}
        >
          <h2>Direct Access Disabled</h2>
          <p>This application can only be accessed through the authorized business application.</p>
          <p>Please contact your administrator for access.</p>
        </div>
      );
    }

    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Navigate to embedded path if needed, preserving query params
  if (shouldNavigateToEmbed) {
    const search = window.location.search;
    return <Navigate to={`${embedPath}${search}`} replace />;
  }

  const appBody = (
    <>
      {!hideNav && <Navigation />}
      <div className={hideNav ? "main-container-no-nav" : "main-container"}>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/radar" element={<RadarView />} />
          <Route path="/updates" element={<UpdatesView />} />
          <Route path="/viewer/:fqn" element={<ViewerView />} />
          <Route path="/profile/:profileFqn/*" element={<ProfileRedirect />} />
          <Route path="/resource/:fileId" element={<ResourceView />} />
          <Route path="/article/:articleFqn" element={<ArticleRedirect />} />
          {/* Spark Routes */}
          <Route path="/spark" element={<SparkInboxView />} />
          <Route path="/spark/search" element={<CreativeSearchView />} />
          <Route path="/spark/project/:projectFqn" element={<SparkProjectView />} />
          <Route path="/spark/library/:itemFqn" element={<LibraryItemEditorPage />} />
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </div>
    </>
  );

  // Wrap Spark routes with the polling provider (global Spark polling)
  const isSparkRoute = location.pathname.startsWith('/spark');

  return isSparkRoute ? (
    <SparkPollingProvider>
      {appBody}
    </SparkPollingProvider>
  ) : (
    appBody
  );
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className={`app ${isAuthenticated ? 'authenticated' : ''}`}>
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<LoginView />} />
        <Route path="/register" element={<RegisterView />} />
        <Route path="/add-tenant" element={<AddTenantView />} />
        {/* All other routes go through AuthenticatedApp */}
        <Route path="*" element={<AuthenticatedApp />} />
      </Routes>
    </div>
  );
};

// Wrap the entire app with necessary providers
const AppWithProviders: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <IframeAuthProvider>
          <App />
          {/* Add ToastContainer here */}
          <ToastContainer
            position="bottom-right" // Example position
            autoClose={1500}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light" // Or "dark" or "colored"
          />
        </IframeAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppWithProviders;

// Redirect /profile/:profileFqn to /viewer/:fqn preserving location state
function ProfileRedirect() {
  const params = useParams();
  const location = useLocation();
  const profileFqn = params.profileFqn || params['*']?.split('/')[0] || '';
  return <Navigate to={`/viewer/${profileFqn}`} state={location.state} replace />;
}

// Redirect /article/:articleFqn to /viewer/:fqn preserving location state
function ArticleRedirect() {
  const params = useParams();
  const location = useLocation();
  const articleFqn = params.articleFqn || '';
  return <Navigate to={`/viewer/${articleFqn}`} state={location.state} replace />;
}

// Inline page wrapper to render CreativeEditor in item mode for deep links
function LibraryItemEditorPage() {
  const params = useParams();
  const itemFqn = params.itemFqn as string | undefined;
  const navigate = useNavigate();
  if (!itemFqn) return <NotFoundView />;
  return (
    <CreativeEditor
      isOpen={true}
      onClose={() => navigate('/spark/search')}
      editorMode={'item'}
      itemFqn={itemFqn}
    />
  );
}
